import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import {
  Brain,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  FileText,
  Upload,
  Eye,
  X,
  Download,
  Filter,
  Search,
  BarChart3,
  Zap,
  Target,
  MessageSquare
} from 'lucide-react';

const TrainingDashboard = ({ workspaceId, chatbotId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [bulkFAQs, setBulkFAQs] = useState('');
  const [newFAQ, setNewFAQ] = useState({ question: '', answer: '' });
  const [trainingText, setTrainingText] = useState('');
  const [trainingCategory, setTrainingCategory] = useState('general');
  const [feedbackFilter, setFeedbackFilter] = useState('all');

  // Fetch effectiveness stats
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery(
    ['effectiveness-stats', workspaceId, chatbotId],
    () => apiClient.get(`/api/whatsapp/training-analytics/${workspaceId}/${chatbotId}/effectiveness-stats`),
    { enabled: !!workspaceId && !!chatbotId }
  );

  // Fetch low confidence matches
  const { data: lowConfidenceData, isLoading: lowConfLoading, refetch: refetchLowConf } = useQuery(
    ['low-confidence-matches', workspaceId, chatbotId],
    () => apiClient.get(`/api/whatsapp/training-analytics/${workspaceId}/${chatbotId}/low-confidence-matches?limit=20`),
    { enabled: !!workspaceId && !!chatbotId && activeTab === 'gaps' }
  );

  // Fetch unhandled messages
  const { data: unhandledData, isLoading: unhandledLoading, refetch: refetchUnhandled } = useQuery(
    ['unhandled-messages', workspaceId, chatbotId],
    () => apiClient.get(`/api/whatsapp/training-analytics/${workspaceId}/${chatbotId}/unhandled-messages?limit=30`),
    { enabled: !!workspaceId && !!chatbotId && activeTab === 'gaps' }
  );

  // Fetch FAQs
  const { data: faqsData, isLoading: faqsLoading, refetch: refetchFAQs } = useQuery(
    ['faqs', chatbotId],
    () => apiClient.get(`/api/chatbot/${chatbotId}/training/faqs`),
    { enabled: !!chatbotId }
  );

  // Fetch training data
  const { data: trainingData, isLoading: trainingLoading, refetch: refetchTraining } = useQuery(
    ['training-data', chatbotId],
    () => apiClient.get(`/api/chatbot/${chatbotId}/training/text`),
    { enabled: !!chatbotId && activeTab === 'training' }
  );

  // Add new FAQ
  const addFAQMutation = useMutation(
    (faqData) => apiClient.post(`/api/chatbot/${chatbotId}/training/faqs`, faqData),
    {
      onSuccess: () => {
        toast.success('FAQ added successfully!');
        setNewFAQ({ question: '', answer: '' });
        refetchFAQs();
      },
      onError: () => toast.error('Failed to add FAQ')
    }
  );

  // Bulk import FAQs
  const bulkImportMutation = useMutation(
    (faqs) => apiClient.post(`/api/chatbot/${chatbotId}/training/faqs/import`, faqs),
    {
      onSuccess: (response) => {
        toast.success(`${response.data.imported} FAQs imported successfully!`);
        setBulkFAQs('');
        refetchFAQs();
      },
      onError: () => toast.error('Failed to import FAQs')
    }
  );

  // Add training text
  const addTrainingMutation = useMutation(
    (data) => apiClient.post(`/api/chatbot/${chatbotId}/training/text`, data),
    {
      onSuccess: () => {
        toast.success('Training data added successfully!');
        setTrainingText('');
        setTrainingCategory('general');
        refetchTraining();
      },
      onError: () => toast.error('Failed to add training data')
    }
  );

  // Delete FAQ
  const deleteFAQMutation = useMutation(
    (faqId) => apiClient.delete(`/api/chatbot/${chatbotId}/training/faqs/${faqId}`),
    {
      onSuccess: () => {
        toast.success('FAQ deleted successfully!');
        setSelectedFAQ(null);
        refetchFAQs();
      },
      onError: () => toast.error('Failed to delete FAQ')
    }
  );

  // Delete training text
  const deleteTrainingMutation = useMutation(
    (textId) => apiClient.delete(`/api/chatbot/${chatbotId}/training/text/${textId}`),
    {
      onSuccess: () => {
        toast.success('Training data deleted!');
        refetchTraining();
      },
      onError: () => toast.error('Failed to delete training data')
    }
  );

  // Create FAQ from feedback
  const createFAQFromFeedbackMutation = useMutation(
    (data) => apiClient.post(`/api/whatsapp/training-analytics/${workspaceId}/${chatbotId}/create-faq-from-feedback`, data),
    {
      onSuccess: () => {
        toast.success('FAQ created from feedback!');
        refetchFAQs();
        refetchStats();
      },
      onError: () => toast.error('Failed to create FAQ')
    }
  );

  const stats = statsData?.data;
  const faqsList = faqsData?.data || [];
  const unhandledMessages = unhandledData?.data?.unhandled || [];
  const lowConfidenceMatches = lowConfidenceData?.data?.matches || [];
  const trainingItems = trainingData?.data || [];

  const handleAddFAQ = () => {
    if (!newFAQ.question.trim() || !newFAQ.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }
    addFAQMutation.mutate(newFAQ);
  };

  const handleBulkImport = () => {
    try {
      const faqs = JSON.parse(bulkFAQs);
      if (!Array.isArray(faqs)) {
        toast.error('Please provide a JSON array of FAQs');
        return;
      }
      bulkImportMutation.mutate(faqs);
    } catch (e) {
      toast.error('Invalid JSON format');
    }
  };

  const handleAddTraining = () => {
    if (!trainingText.trim()) {
      toast.error('Please enter training text');
      return;
    }
    addTrainingMutation.mutate({
      text: trainingText,
      category: trainingCategory
    });
  };

  const handleImportJSON = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const faqs = JSON.parse(e.target.result);
        setBulkFAQs(JSON.stringify(faqs, null, 2));
      } catch {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const filteredFAQs = faqsList.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'faqs', name: 'FAQs', icon: MessageSquare },
    { id: 'training', name: 'Training Data', icon: Brain },
    { id: 'gaps', name: 'Training Gaps', icon: AlertCircle },
    { id: 'feedback', name: 'Feedback', icon: Zap }
  ];

  const handleRefreshAll = () => {
    refetchStats();
    refetchFAQs();
    refetchLowConf();
    refetchUnhandled();
    toast.success('Data refreshed!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Training Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage FAQs, monitor effectiveness, and continuously improve your model
          </p>
        </div>
        <button
          onClick={handleRefreshAll}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh All
        </button>
      </div>

      {/* Metrics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Helpful Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(stats.overallStats?.helpfulRate * 100 || 0).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Avg Confidence</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(stats.overallStats?.avgConfidence || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <MessageSquare className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total Interactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.overallStats?.totalInteractions || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total FAQs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {faqsList.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {stats?.recommendations && stats.recommendations.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                    <Zap className="h-5 w-5 mr-2" />
                    AI Recommendations
                  </h3>
                  <ul className="space-y-2">
                    {stats.recommendations.map((rec, idx) => (
                      <li key={idx} className="text-sm text-blue-800 flex items-start">
                        <span className="mr-2">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {stats?.trainingGaps && stats.trainingGaps.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-900 mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Top Training Gaps
                  </h3>
                  <div className="space-y-2">
                    {stats.trainingGaps.slice(0, 5).map((gap, idx) => (
                      <div key={idx} className="text-sm text-yellow-800">
                        <p className="font-medium">{gap.question}</p>
                        <p className="text-xs text-yellow-700">
                          Frequency: {gap.frequency} requests
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stats?.mostHelpfulFaqs && stats.mostHelpfulFaqs.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-3">Top Performing FAQs</h3>
                  <div className="space-y-2">
                    {stats.mostHelpfulFaqs.slice(0, 5).map((faq) => (
                      <div key={faq.faqId} className="text-sm text-green-800">
                        <p className="font-medium">{faq.question}</p>
                        <p className="text-xs text-green-700">
                          {faq.helpfulCount}/{faq.matchCount} helpful
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FAQS TAB */}
          {activeTab === 'faqs' && (
            <div className="space-y-6">
              {/* Add Single FAQ */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Add New FAQ</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question
                    </label>
                    <input
                      type="text"
                      value={newFAQ.question}
                      onChange={(e) => setNewFAQ({ ...newFAQ, question: e.target.value })}
                      placeholder="e.g., How do I reset my password?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Answer
                    </label>
                    <textarea
                      rows={3}
                      value={newFAQ.answer}
                      onChange={(e) => setNewFAQ({ ...newFAQ, answer: e.target.value })}
                      placeholder="e.g., Click Forgot Password on login page, enter your email..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleAddFAQ}
                    disabled={addFAQMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add FAQ
                  </button>
                </div>
              </div>

              {/* Bulk Import */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Bulk Import FAQs</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <label className="flex-1 flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400">
                      <div className="text-center">
                        <Upload className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">Upload JSON file</p>
                      </div>
                      <input
                        type="file"
                        accept=".json"
                        onChange={(e) => e.target.files && handleImportJSON(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Or paste JSON array:
                    </label>
                    <textarea
                      rows={6}
                      value={bulkFAQs}
                      onChange={(e) => setBulkFAQs(e.target.value)}
                      placeholder='[{"question":"How to...?", "answer":"..."}, ...]'
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                    />
                  </div>

                  <button
                    onClick={handleBulkImport}
                    disabled={bulkImportMutation.isLoading || !bulkFAQs.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Import FAQs
                  </button>
                </div>
              </div>

              {/* Search FAQs */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* FAQs List */}
              <div className="space-y-3">
                {faqsLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                  </div>
                ) : filteredFAQs.length > 0 ? (
                  filteredFAQs.map((faq) => (
                    <div
                      key={faq.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{faq.question}</p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{faq.answer}</p>
                        </div>
                        <button
                          onClick={() => deleteFAQMutation.mutate(faq.id)}
                          disabled={deleteFAQMutation.isLoading}
                          className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No FAQs found. Create one to get started!
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TRAINING DATA TAB */}
          {activeTab === 'training' && (
            <div className="space-y-6">
              {/* Add Training Data */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Add Training Data</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={trainingCategory}
                      onChange={(e) => setTrainingCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="general">General Knowledge</option>
                      <option value="hours">Business Hours & Operations</option>
                      <option value="product">Product Information</option>
                      <option value="support">Support & Help</option>
                      <option value="pricing">Pricing & Plans</option>
                      <option value="meta_ads">Meta Ads Variations</option>
                      <option value="automation">Automation Patterns</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Training Text
                    </label>
                    <textarea
                      rows={5}
                      value={trainingText}
                      onChange={(e) => setTrainingText(e.target.value)}
                      placeholder="Enter context, examples, and information the AI should learn from..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleAddTraining}
                    disabled={addTrainingMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Training Data
                  </button>
                </div>
              </div>

              {/* Training Data List */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Existing Training Data</h3>
                {trainingLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                  </div>
                ) : trainingItems.length > 0 ? (
                  trainingItems.map((item) => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {item.category || 'general'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-3">{item.text}</p>
                        </div>
                        <button
                          onClick={() => deleteTrainingMutation.mutate(item.id)}
                          disabled={deleteTrainingMutation.isLoading}
                          className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No training data yet. Add some to improve model responses!
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TRAINING GAPS TAB */}
          {activeTab === 'gaps' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Unhandled Messages */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Unhandled Messages (Highest Priority)
                  </h3>
                  {unhandledLoading ? (
                    <div className="text-center py-6">
                      <RefreshCw className="mx-auto h-6 w-6 text-gray-400 animate-spin" />
                    </div>
                  ) : unhandledMessages.length > 0 ? (
                    <div className="space-y-2">
                      {unhandledMessages.slice(0, 10).map((msg, idx) => (
                        <div key={idx} className="bg-white p-3 rounded text-sm">
                          <p className="font-medium text-gray-900">"{msg.message}"</p>
                          <p className="text-xs text-red-700 mt-1">
                            Asked {msg.count} times
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-red-800">All messages are handled! Great job!</p>
                  )}
                </div>

                {/* Low Confidence Matches */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-900 mb-4 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Low Confidence Matches (Need Improvement)
                  </h3>
                  {lowConfLoading ? (
                    <div className="text-center py-6">
                      <RefreshCw className="mx-auto h-6 w-6 text-gray-400 animate-spin" />
                    </div>
                  ) : lowConfidenceMatches.length > 0 ? (
                    <div className="space-y-2">
                      {lowConfidenceMatches.slice(0, 10).map((match, idx) => (
                        <div key={idx} className="bg-white p-3 rounded text-sm">
                          <p className="font-medium text-gray-900">"{match.incomingMessage}"</p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Confidence: {(match.similarity * 100).toFixed(0)}%
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-800">All matches have good confidence!</p>
                  )}
                </div>
              </div>

              {/* Action Items */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3">Next Steps</h3>
                <ol className="space-y-2 text-sm text-blue-800">
                  <li>1. Create FAQs for <strong>unhandled messages</strong> (red section above)</li>
                  <li>2. Improve FAQ questions for <strong>low confidence matches</strong> (yellow section above)</li>
                  <li>3. Add more training data in the <strong>Training Data tab</strong></li>
                  <li>4. Check back weekly to see improvement</li>
                </ol>
              </div>
            </div>
          )}

          {/* FEEDBACK TAB */}
          {activeTab === 'feedback' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">How Feedback Works</h3>
                <p className="text-sm text-blue-800">
                  Users mark auto-replies as helpful or unhelpful. Unhelpful feedback with corrections are shown here.
                  Create FAQs from these corrections to improve the model.
                </p>
              </div>

              {/* Sample Feedback Display */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">Recent Feedback</h3>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 text-center py-8">
                    Feedback data will appear here as users interact with auto-replies.
                    Use the API endpoints to integrate feedback collection in your chat interface.
                  </p>
                </div>
              </div>

              {/* Integration Guide */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Integration Guide</h3>
                <p className="text-sm text-gray-700 mb-4">
                  To collect feedback from your chat users, add this code to your chat interface:
                </p>
                <div className="bg-gray-900 text-gray-100 rounded p-4 text-xs font-mono overflow-auto">
                  <pre>{`// After auto-reply is shown to user:
const recordFeedback = async (helpful, userCorrection) => {
  await fetch('/api/whatsapp/training-analytics/\${wsId}/\${cbId}/analyze-feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      incomingMessage: userMessage,
      matchedFaqId: faqId,
      matchedAnswer: autoReplyText,
      wasHelpful: helpful,
      similarity: confidenceScore,
      userCorrectedAnswer: userCorrection
    })
  });
};

// Show feedback buttons after auto-reply
<button onClick={() => recordFeedback(true)}>👍 Helpful</button>
<button onClick={() => recordFeedback(false)}>👎 Not helpful</button>`}</pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingDashboard;
